import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalendarGrid from '../CalendarGrid';
import ReservationForm from './ReservationForm';
import { ROOMS, RESERVATION_STATUS } from '../../Constants/rooms';
import {
    adminLogin, clearAdminToken, getAdminToken,
    fetchReservations, createReservation, updateReservation, deleteReservation,
} from '../../Util/reservationApi';
import { diffNights, formatKoreanDate, nightsBetween, shiftMonth, todayYmd } from '../../Util/dateUtil';
import '../../CSS/style/Availability.css';
import '../../CSS/style/AdminReservation.css';

const statusLabel = (key) =>
    (RESERVATION_STATUS.find(s => s.key === key) || {}).label || key;

const now = new Date();
const THIS_MONTH = { year: now.getFullYear(), month: now.getMonth() + 1 };

/* ── 비밀번호 입력 화면 ── */
const LoginGate = ({ onSuccess }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!password.trim() || busy) return;
        setBusy(true);
        setError(null);
        try {
            await adminLogin(password);
            onSuccess();
        } catch (err) {
            setError(err.message || '로그인에 실패했습니다.');
            setPassword('');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="admin-login">
            <form className="admin-login-box" onSubmit={submit}>
                <h1>예약 관리자</h1>
                <p>관리자 비밀번호를 입력해 주세요.</p>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호"
                    autoFocus
                    autoComplete="current-password"
                />
                {error && <span className="admin-login-error">{error}</span>}
                <button type="submit" disabled={busy}>{busy ? '확인 중…' : '로그인'}</button>
            </form>
        </div>
    );
};

const AdminReservation = () => {
    const [authed, setAuthed]   = useState(() => Boolean(getAdminToken()));
    const [cursor, setCursor]   = useState(THIS_MONTH);
    const [rows, setRows]       = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);
    const [editing, setEditing] = useState(null);   // { mode: 'create'|'edit', data }
    const [notice, setNotice]   = useState(null);

    const cursorRef = useRef(cursor);
    cursorRef.current = cursor;

    const handleAuthError = useCallback((err) => {
        if (err.status === 401 || err.status === 403) {
            clearAdminToken();
            setAuthed(false);
            return true;
        }
        return false;
    }, []);

    const load = useCallback(async ({ year, month }, signal) => {
        setLoading(true);
        try {
            const res = await fetchReservations(year, month, signal);
            if (cursorRef.current.year !== year || cursorRef.current.month !== month) return;
            setRows(res.reservations || []);
            setError(null);
        } catch (err) {
            if (err.name === 'AbortError') return;
            if (!handleAuthError(err)) setError(err.message || '예약 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [handleAuthError]);

    useEffect(() => {
        if (!authed) return;
        const controller = new AbortController();
        load(cursor, controller.signal);
        return () => controller.abort();
    }, [authed, cursor, load]);

    // 알림 문구는 3초 뒤 사라집니다.
    useEffect(() => {
        if (!notice) return;
        const timer = setTimeout(() => setNotice(null), 3000);
        return () => clearTimeout(timer);
    }, [notice]);

    /** 달력 점 표시용 — 취소 건은 자리를 비웁니다. */
    const bookedByDate = useMemo(() => {
        const map = {};
        rows.filter(r => r.status !== 'CANCELED').forEach(({ room, checkIn, checkOut }) => {
            nightsBetween(checkIn, checkOut).forEach(ymd => {
                if (!map[ymd]) map[ymd] = [];
                if (!map[ymd].includes(room)) map[ymd].push(room);
            });
        });
        return map;
    }, [rows]);

    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => (a.checkIn === b.checkIn
            ? ROOMS.findIndex(r => r.key === a.room) - ROOMS.findIndex(r => r.key === b.room)
            : a.checkIn.localeCompare(b.checkIn))),
        [rows],
    );

    const selectedRows = useMemo(
        () => (selected ? sortedRows.filter(r => nightsBetween(r.checkIn, r.checkOut).includes(selected)) : []),
        [selected, sortedRows],
    );

    const move = (delta) => {
        setSelected(null);
        setCursor(prev => shiftMonth(prev.year, prev.month, delta));
    };

    const save = async (payload) => {
        try {
            if (editing.mode === 'edit') {
                await updateReservation(editing.data.id, payload);
                setNotice('예약을 수정했습니다.');
            } else {
                await createReservation(payload);
                setNotice('예약을 등록했습니다.');
            }
            setEditing(null);
            await load(cursor);
        } catch (err) {
            if (handleAuthError(err)) return;
            throw err;   // 모달 안에서 에러 문구로 표시
        }
    };

    const remove = async (row) => {
        const ok = window.confirm(
            `${row.checkIn} · ${row.room} · ${row.guestName || '이름없음'}\n\n이 예약을 삭제할까요? 되돌릴 수 없습니다.`,
        );
        if (!ok) return;
        try {
            await deleteReservation(row.id);
            setNotice('예약을 삭제했습니다.');
            await load(cursor);
        } catch (err) {
            if (handleAuthError(err)) return;
            setError(err.message || '삭제에 실패했습니다.');
        }
    };

    const logout = () => {
        clearAdminToken();
        setAuthed(false);
        setRows([]);
    };

    if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;

    return (
        <div className="admin-wrap">
            <div className="admin-header">
                <div>
                    <h1>예약 관리</h1>
                    <p>달력에서 날짜를 선택하면 해당 날짜의 예약을 확인·추가할 수 있습니다.</p>
                </div>
                <div className="admin-header-actions">
                    <button type="button" className="admin-btn admin-btn--primary" onClick={() => setEditing({ mode: 'create', data: { checkIn: selected || todayYmd() } })}>+ 예약 등록</button>
                    <button type="button" className="admin-btn" onClick={logout}>로그아웃</button>
                </div>
            </div>

            {notice && <div className="admin-notice">{notice}</div>}
            {error && <div className="admin-notice admin-notice--error">{error}</div>}

            <div className="admin-panel">
                <div className="avail-toolbar">
                    <button type="button" className="avail-nav" onClick={() => move(-1)} aria-label="이전 달">‹</button>
                    <div className="avail-month">
                        <strong>{cursor.year}년 {cursor.month}월</strong>
                        <span className="avail-updated">{rows.length}건</span>
                    </div>
                    <button type="button" className="avail-nav" onClick={() => move(1)} aria-label="다음 달">›</button>
                </div>

                <div className={`avail-grid-wrap${loading ? ' is-loading' : ''}`}>
                    <CalendarGrid
                        year={cursor.year}
                        month={cursor.month}
                        bookedByDate={bookedByDate}
                        selectedDate={selected}
                        onDayClick={(ymd) => setSelected(prev => (prev === ymd ? null : ymd))}
                        dimPast={false}
                    />
                    {loading && <div className="avail-spinner" aria-label="불러오는 중" />}
                </div>
            </div>

            {selected && (
                <div className="admin-panel">
                    <div className="admin-panel-head">
                        <h2>{formatKoreanDate(selected)} 예약</h2>
                        <button
                            type="button"
                            className="admin-btn admin-btn--primary admin-btn--sm"
                            onClick={() => setEditing({ mode: 'create', data: { checkIn: selected } })}
                        >이 날짜로 등록</button>
                    </div>
                    {selectedRows.length === 0 ? (
                        <p className="admin-empty">해당 날짜에 등록된 예약이 없습니다. (6개 객실 모두 예약가능)</p>
                    ) : (
                        <ul className="admin-day-list">
                            {selectedRows.map(row => (
                                <li key={row.id} className={row.status === 'CANCELED' ? 'is-canceled' : ''}>
                                    <span className="admin-day-room">{row.room}</span>
                                    <span className="admin-day-guest">{row.guestName || '-'}</span>
                                    <span className="admin-day-phone">{row.phone || '-'}</span>
                                    <span className={`admin-status admin-status--${row.status}`}>{statusLabel(row.status)}</span>
                                    <span className="admin-day-range">{row.checkIn} → {row.checkOut}</span>
                                    <span className="admin-day-actions">
                                        <button type="button" className="admin-btn admin-btn--sm" onClick={() => setEditing({ mode: 'edit', data: row })}>수정</button>
                                        <button type="button" className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => remove(row)}>삭제</button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className="admin-panel">
                <div className="admin-panel-head">
                    <h2>{cursor.month}월 전체 예약</h2>
                </div>
                {sortedRows.length === 0 ? (
                    <p className="admin-empty">이번 달 예약이 없습니다.</p>
                ) : (
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                            <tr>
                                <th>객실</th><th>예약자</th><th>연락처</th><th>입실</th><th>퇴실</th>
                                <th>박</th><th>인원</th><th>상태</th><th>메모</th><th>관리</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedRows.map(row => (
                                <tr key={row.id} className={row.status === 'CANCELED' ? 'is-canceled' : ''}>
                                    <td>{row.room}</td>
                                    <td>{row.guestName || '-'}</td>
                                    <td>{row.phone || '-'}</td>
                                    <td>{row.checkIn}</td>
                                    <td>{row.checkOut}</td>
                                    <td>{diffNights(row.checkIn, row.checkOut)}</td>
                                    <td>{row.headcount || '-'}</td>
                                    <td><span className={`admin-status admin-status--${row.status}`}>{statusLabel(row.status)}</span></td>
                                    <td className="admin-memo">{row.memo || ''}</td>
                                    <td className="admin-td-actions">
                                        <button type="button" className="admin-btn admin-btn--sm" onClick={() => setEditing({ mode: 'edit', data: row })}>수정</button>
                                        <button type="button" className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => remove(row)}>삭제</button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editing && (
                <ReservationForm
                    mode={editing.mode}
                    initial={editing.data}
                    onSubmit={save}
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
};

export default AdminReservation;